import { useState, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronDown, Info, Edit2, Save, X, CheckCircle, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function SourcesMatrixTab() {
  const { data: matrixData, isLoading, refetch } = trpc.ergos.getCurrentSourcesMatrix.useQuery();
  const saveMutation = trpc.ergos.saveSourcesMatrixDraft.useMutation();
  const cancelMutation = trpc.ergos.cancelSourcesMatrixDraft.useMutation();
  const publishMutation = trpc.ergos.publishSourcesMatrix.useMutation();
  const cloneMutation = trpc.ergos.cloneSourcesMatrixForEdit.useMutation();
  
  const [mounted, setMounted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [selectedModules, setSelectedModules] = useState<Record<number, number[]>>({});
  const [expandedAgent, setExpandedAgent] = useState<number | null>(null);
  const [dropdownDirection, setDropdownDirection] = useState<Record<number, 'down' | 'up'>>({});
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (matrixData?.agents) {
      const initialSelection: Record<number, number[]> = {};
      matrixData.agents.forEach((agent: any) => {
        initialSelection[agent.id] = agent.selectedModuleIds || [];
      });
      setSelectedModules(initialSelection);
    }
  }, [matrixData]);

  const handleEditClick = async () => {
    // Se a versão está PUBLISHED, criar clone para edição
    if (matrixData?.status === "PUBLISHED") {
      try {
        const toastId = toast.loading("Criando nova versão para edição...");
        const result = await cloneMutation.mutateAsync({ versionId: matrixData.versionId });
        toast.dismiss(toastId);
        
        if (result.isClone) {
          toast.success(`Nova versão ${result.newVersion} criada para edição`);
        }
        
        await refetch();
        setIsEditing(true);
      } catch (error: any) {
        toast.error("Erro ao criar versão para edição", {
          description: error.message || "Ocorreu um erro inesperado.",
        });
      }
    } else {
      setIsEditing(true);
    }
  };

  const handleCancelClick = async () => {
    try {
      await cancelMutation.mutateAsync({ versionId: matrixData?.versionId });
      setIsEditing(false);
      refetch();
      toast.success("Edição cancelada", {
        description: "As alterações foram descartadas.",
      });
    } catch (error: any) {
      toast.error("Erro ao cancelar", {
        description: error.message || "Ocorreu um erro inesperado.",
      });
    }
  };

  const handleSaveClick = async () => {
    try {
      const agentModules = matrixData?.agents.map((agent: any) => ({
        agentId: agent.id,
        moduleIds: selectedModules[agent.id] || [],
      })) || [];

      await saveMutation.mutateAsync({
        versionId: matrixData?.versionId,
        agentModules,
      });

      setIsEditing(false);
      await refetch();
      toast.success("Matriz salva com sucesso!", {
        description: "Os módulos foram vinculados aos agentes.",
      });
    } catch (error: any) {
      toast.error("Erro ao salvar", {
        description: error.message || "Ocorreu um erro ao salvar a matriz.",
      });
    }
  };

  const handlePublishClick = async () => {
    if (!matrixData?.versionId) return;
    
    setIsPublishing(true);
    try {
      const result = await publishMutation.mutateAsync({ versionId: matrixData.versionId });
      toast.success(result.message || "Matriz publicada com sucesso!", {
        description: `Versão ${result.version} agora está ativa.`,
      });
      await refetch();
    } catch (error: any) {
      const errorMsg = error.message || "Ocorreu um erro ao publicar.";
      if (errorMsg.includes("VALIDATION_ERROR")) {
        toast.error("Validação falhou", {
          description: errorMsg.replace("VALIDATION_ERROR: ", ""),
        });
      } else {
        toast.error("Erro ao publicar", {
          description: errorMsg,
        });
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const toggleModule = (agentId: number, moduleId: number) => {
    setSelectedModules((prev) => {
      const current = prev[agentId] || [];
      const updated = current.includes(moduleId)
        ? current.filter((id) => id !== moduleId)
        : [...current, moduleId];
      return { ...prev, [agentId]: updated };
    });
  };

  const handleDropdownToggle = (agentId: number) => {
    if (expandedAgent === agentId) {
      setExpandedAgent(null);
      return;
    }

    setExpandedAgent(agentId);

    // Calcular se o dropdown deve abrir para cima ou para baixo
    setTimeout(() => {
      const dropdownElement = dropdownRefs.current[agentId];
      if (dropdownElement) {
        const rect = dropdownElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 240; // max-height do dropdown

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          setDropdownDirection((prev) => ({ ...prev, [agentId]: 'up' }));
        } else {
          setDropdownDirection((prev) => ({ ...prev, [agentId]: 'down' }));
        }
      }
    }, 0);
  };

  if (!mounted) return null;

  const isPublished = matrixData?.status === "PUBLISHED";
  const isDraft = matrixData?.status === "DRAFT";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Matriz de Fontes Geradoras</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isEditing ? (
              <span className="text-amber-600 font-medium">Você está editando a Matriz de Fontes Geradoras</span>
            ) : (
              <span className="flex items-center gap-2">
                Versão {matrixData?.version || "1.0"} - 
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  isPublished 
                    ? "bg-green-100 text-green-800" 
                    : "bg-amber-100 text-amber-800"
                }`}>
                  {isPublished && <CheckCircle size={12} />}
                  {matrixData?.status || "DRAFT"}
                </span>
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditing && (
            <>
              <Button onClick={handleEditClick} className="gap-2" disabled={cloneMutation.isPending}>
                {cloneMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Edit2 size={16} />
                )}
                {isPublished ? "Editar (criar nova versão)" : "Editar Matriz"}
              </Button>
              {isDraft && (
                <Button 
                  onClick={handlePublishClick} 
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  disabled={isPublishing}
                >
                  {isPublishing ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Upload size={16} />
                  )}
                  Publicar
                </Button>
              )}
            </>
          )}
          {isEditing && (
            <>
              <Button onClick={handleSaveClick} disabled={saveMutation.isPending} className="gap-2 bg-green-600 hover:bg-green-700">
                <Save size={16} />
                Salvar
              </Button>
              <Button onClick={handleCancelClick} variant="outline" disabled={cancelMutation.isPending} className="gap-2">
                <X size={16} />
                Cancelar
              </Button>
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : !matrixData?.agents || matrixData.agents.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Nenhum agente configurado</p>
        </Card>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Agente</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Módulos Vinculados</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {matrixData.agents.map((agent: any) => (
                  <tr key={agent.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">{agent.agentLabel}</td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="relative" ref={(el) => { if (el) dropdownRefs.current[agent.id] = el; }}>
                          <button
                            onClick={() => handleDropdownToggle(agent.id)}
                            className="flex items-center gap-2 px-3 py-2 border rounded bg-white hover:bg-gray-50 text-sm w-full justify-between"
                          >
                            <span>Selecionar módulos</span>
                            <ChevronDown size={16} className={`transition-transform ${expandedAgent === agent.id ? "rotate-180" : ""}`} />
                          </button>
                          {expandedAgent === agent.id && (
                            <div className={`absolute left-0 w-64 bg-white border rounded shadow-lg z-20 p-3 space-y-2 max-h-60 overflow-y-auto ${
                              dropdownDirection[agent.id] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
                            }`}>
                              {matrixData.availableModules?.map((module: any) => (
                                <label key={module.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                  <input
                                    type="checkbox"
                                    checked={(selectedModules[agent.id] || []).includes(module.id)}
                                    onChange={() => toggleModule(agent.id, module.id)}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm">{module.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                          {(selectedModules[agent.id] || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {(selectedModules[agent.id] || []).map((moduleId: number) => {
                                const module = matrixData.availableModules?.find((m: any) => m.id === moduleId);
                                return (
                                  <span key={moduleId} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                                    {module?.name}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(agent.selectedModuleIds || []).length > 0 ? (
                            agent.selectedModuleIds.map((moduleId: number) => {
                              const module = matrixData.availableModules?.find((m: any) => m.id === moduleId);
                              return (
                                <span key={moduleId} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                                  {module?.name}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground text-sm">Nenhum módulo vinculado</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${agent.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                        {agent.isActive ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Info Box - Regra de Geração das Fontes Geradoras */}
          <Alert className="mt-6 border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900 ml-2">
              <strong>Regra de Geração das Fontes Geradoras</strong>
              <p className="mt-2 text-sm leading-relaxed">
                Os módulos vinculados a cada agente são definidos automaticamente com base nas respostas do Questionário ERGOS. Sempre que ao menos uma pergunta de um módulo receber pontuação 4, esse módulo passa a ser considerado fonte geradora de risco para os agentes correspondentes, conforme a metodologia ERGOS.
              </p>
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}
